/**
 * ONLY · PLP (sections/on-main-collection.liquid + snippets/on-product-card.liquid).
 * - <on-collection>: selector de columnas (recordado en localStorage), «VER MÁS» que suma
 *   la página siguiente sin recargar (sin JS es un link a ?page=N; al volver desde la ficha se
 *   vuelven a sumar las páginas cargadas y el scroll) y nombre de la banda del
 *   header para el diff por sección (el Figma de la PLP la llama «content-02» en desktop).
 * - <on-sort>: «Ordenar por» como disclosure con links; conserva los filtros de la URL.
 * - <on-filters>: panel de filtros (snippets/on-filters.liquid) como diálogo modal superpuesto
 *   con velo; cada filtro se aplica apenas se elige, con el panel abierto, mediante el
 *   <facets-form-component> de Horizon (misma URL, re-render de la sección y evento estándar de
 *   colección). Atrás y adelante vuelven a dibujar la sección con los filtros de la URL.
 * - <on-product-card>: carrusel de fotos con flechas que se ocultan sin destino, compra
 *   rápida por talle con /cart/add.js y el evento estándar de Shopify (header y carrito de
 *   Horizon se enteran solos).
 * Horizon re-renderiza la sección al filtrar (morph): todo se engancha por delegación y se
 * vuelve a aplicar lo que el HTML del servidor no trae (columnas elegidas).
 */

import { sectionRenderer } from '@theme/section-renderer';
import { lockScroll, prefersReducedMotion, unlockScroll } from '@theme/utilities';

const STORAGE = { d: 'on-plp-cols-d', m: 'on-plp-cols-m' };

/* Pausa al escribir o mover el precio antes de aplicarlo (only.com.uy espera 500 ms). */
const PRICE_DELAY = 500;

/**
 * Filtros y orden de una búsqueda en un orden fijo, para saber si lo elegido cambió respecto de lo
 * que está dibujado.
 * @param {URLSearchParams} params
 */
function filterQuery(params) {
  return [...params]
    .filter(([key, value]) => value !== '' && (key.startsWith('filter.') || key === 'sort_by'))
    .map(([key, value]) => `${key}=${value}`)
    .sort()
    .join('&');
}

/* Desde 990 px Horizon no scrollea la ventana sino .page-wrapper (base.css: html y body con
   overflow hidden), así que el scroll que se guarda y se restaura es el de ese contenedor. */
function pageScroller() {
  const wrapper = document.querySelector('.page-wrapper');
  return wrapper && window.matchMedia('(min-width: 990px)').matches ? wrapper : null;
}

function pageScrollTop() {
  return pageScroller()?.scrollTop ?? window.scrollY;
}
const VALID_COLS = { d: ['3', '4'], m: ['1', '2', '4'] };

function readCols(viewport) {
  try {
    const value = localStorage.getItem(STORAGE[viewport]);
    return VALID_COLS[viewport].includes(value) ? value : null;
  } catch {
    return null;
  }
}

class OnCollection extends HTMLElement {
  connectedCallback() {
    this.addEventListener('click', this.onClick);
    this.applyCols();
    this.observer = new MutationObserver(() => {
      this.applyCols();
      this.watchFilterStop();
    });
    this.observer.observe(this, { childList: true, subtree: true });
    this.labelHeader();
    this.watchFilterStop();
    // El scroll llega en captura: desde 990 px scrollea .page-wrapper (Horizon), debajo el documento.
    document.addEventListener('scroll', this.onPageScroll, { capture: true, passive: true });
    this.filterQuery = window.matchMedia('(max-width: 749px)');
    this.filterQuery.addEventListener('change', this.resetFilterStop);
    this.restore();
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.onClick);
    this.observer?.disconnect();
    this.filterObserver?.disconnect();
    this.filterQuery?.removeEventListener('change', this.resetFilterStop);
    document.removeEventListener('scroll', this.onPageScroll, { capture: true });
    if (this.scrollFrame) cancelAnimationFrame(this.scrollFrame);
    this.headerQuery?.removeEventListener('change', this.setHeaderName);
    window.removeEventListener('pagehide', this.saveScroll);
  }

  /* FILTRAR flotante (QA 7, Gio 23/10): se va cuando el final del listado ([data-on-filter-stop],
     al pie de la grilla y del scroll infinito) llega a la altura del botón, así no tapa la última
     fila, el newsletter ni el footer; con cada tanda nueva el final baja y el botón vuelve. En
     mobile además se va mientras se baja (no queda sobre el nombre ni el precio de las tarjetas) y
     vuelve al subir. Arriba de todo siempre se ve: con pocos resultados es la única forma de filtrar. */
  get filterButton() {
    return /** @type {HTMLElement | null} */ (this.querySelector('[data-on-filters-open]'));
  }

  /* Los filtros re-dibujan la sección y pueden traer otro final u otro botón: se los vuelve a tomar. */
  watchFilterStop() {
    const stop = this.querySelector('[data-on-filter-stop]');
    const button = this.filterButton;
    if (stop === this.filterStop && button === this.filterButtonSeen) return;
    this.filterButtonSeen = button;
    if (stop === this.filterStop) {
      this.syncFilterButton();
      return;
    }
    this.filterObserver?.disconnect();
    this.filterObserver = null;
    this.filterStop = stop;
    this.endPassed = false;
    if (!stop || !button) {
      this.syncFilterButton();
      return;
    }
    // La línea es el borde de abajo del botón (23 px del borde de la pantalla en desktop, 20 en mobile).
    const bottom = Math.round(parseFloat(getComputedStyle(button).bottom)) || 0;
    this.filterObserver = new IntersectionObserver(
      (entries) => {
        const entry = entries[entries.length - 1];
        const line = entry.rootBounds?.bottom ?? window.innerHeight - bottom;
        this.endPassed = entry.boundingClientRect.top < line;
        this.syncFilterButton();
      },
      { rootMargin: `0px 0px -${bottom}px 0px` }
    );
    this.filterObserver.observe(stop);
  }

  resetFilterStop = () => {
    this.filterStop = null;
    this.watchFilterStop();
  };

  /* Sólo el scroll de la página: los carruseles de las tarjetas y el panel scrollean adentro. */
  onPageScroll = (event) => {
    const target = event.target === document ? document.scrollingElement : event.target;
    if (target !== document.scrollingElement && !target?.classList?.contains('page-wrapper')) return;
    if (this.scrollFrame) return;
    this.scrollFrame = requestAnimationFrame(() => {
      this.scrollFrame = 0;
      this.syncFilterButton();
    });
  };

  /* data-on-away: el final del listado ya llegó al botón. data-on-down: se está bajando (sólo se
     usa en mobile). Con menos de 4 px entre dos cuadros se deja como estaba, para que un temblor
     del dedo no lo haga parpadear. */
  syncFilterButton() {
    const button = this.filterButton;
    if (!button) return;
    if (!this.filterStop) {
      button.removeAttribute('data-on-away');
      button.removeAttribute('data-on-down');
      return;
    }
    const top = pageScrollTop();
    const delta = top - (this.lastScrollTop ?? top);
    this.lastScrollTop = top;
    if (top < 1) this.scrollingDown = false;
    else if (Math.abs(delta) >= 4) this.scrollingDown = delta > 0;

    button.toggleAttribute('data-on-away', Boolean(this.endPassed) && top >= 1);
    button.toggleAttribute('data-on-down', Boolean(this.scrollingDown));
  }

  /* El header es el mismo DOM en los dos viewports; el Figma lo nombra distinto. */
  labelHeader() {
    const desktopName = this.dataset.headerNameDesktop;
    this.header = document.querySelector('.on-header[data-section]');
    if (!desktopName || !this.header) return;
    this.headerQuery = window.matchMedia('(min-width: 750px)');
    this.setHeaderName();
    this.headerQuery.addEventListener('change', this.setHeaderName);
  }

  setHeaderName = () => {
    if (!this.header || !this.headerQuery) return;
    this.header.dataset.section = this.headerQuery.matches ? this.dataset.headerNameDesktop : 'header';
  };

  get grid() {
    return this.querySelector('[data-on-grid]');
  }

  applyCols() {
    const grid = this.grid;
    if (!grid) return;
    for (const viewport of ['d', 'm']) {
      const key = viewport === 'd' ? 'colsD' : 'colsM';
      const stored = readCols(viewport);
      if (stored && grid.dataset[key] !== stored) grid.dataset[key] = stored;
      const current = grid.dataset[key];
      this.querySelectorAll(`[data-on-viewport="${viewport}"]`).forEach((button) => {
        const pressed = String(button.getAttribute('data-on-cols') === current);
        if (button.getAttribute('aria-pressed') !== pressed) button.setAttribute('aria-pressed', pressed);
      });
    }
    // El sizes del servidor es el de 4/2 columnas: con otra vista la foto baja chica.
    const width = (cols) => `${Math.ceil(100 / Number(cols))}vw`;
    const sizes = `(min-width: 750px) ${width(grid.dataset.colsD || 4)}, ${width(grid.dataset.colsM || 2)}`;
    grid.querySelectorAll('img.on-card__image').forEach((img) => {
      if (img.getAttribute('sizes') !== sizes) img.setAttribute('sizes', sizes);
    });
  }

  onClick = (event) => {
    const target = /** @type {HTMLElement} */ (event.target);

    const filtersButton = target.closest('[data-on-filters-open]');
    if (filtersButton) {
      /** @type {OnFilters | null} */ (this.querySelector('on-filters'))?.open(filtersButton);
      return;
    }

    const viewButton = target.closest('[data-on-cols]');
    if (viewButton) {
      const viewport = viewButton.getAttribute('data-on-viewport');
      const cols = viewButton.getAttribute('data-on-cols');
      try {
        localStorage.setItem(STORAGE[viewport], cols);
      } catch {}
      const grid = this.grid;
      if (grid) grid.dataset[viewport === 'd' ? 'colsD' : 'colsM'] = cols;
      this.applyCols();
      return;
    }

    const more = target.closest('[data-on-load-more]');
    if (more) {
      event.preventDefault();
      this.loadMore(/** @type {HTMLAnchorElement} */ (more));
    }
  };

  async loadMore(link) {
    if (link.getAttribute('aria-busy') === 'true') return;
    const href = link.href;

    try {
      if (await this.appendPage(link)) this.remember(href);
    } catch {
      // Si falla, el link navega a la página siguiente.
      window.location.href = href;
    }
  }

  /** @returns {Promise<boolean>} false si la tanda se descartó porque los filtros cambiaron */
  async appendPage(link, { restoring = false } = {}) {
    link.setAttribute('aria-busy', 'true');
    const requested = link.getAttribute('href');

    const url = new URL(link.href, window.location.origin);
    url.searchParams.set('section_id', this.dataset.sectionId);

    const response = await fetch(url.toString());
    if (!response.ok) throw new Error(String(response.status));
    const text = await response.text();
    // Los filtros se aplican al instante con el panel abierto: si mientras llegaba esta tanda la
    // sección se volvió a dibujar (el disparador ya apunta a otra búsqueda), no se suma a la grilla nueva.
    if (!link.isConnected || link.getAttribute('href') !== requested) return false;
    const html = new DOMParser().parseFromString(text, 'text/html');
    // Una tanda puede venir sin tarjetas y con página siguiente (PLP por color: con filtros, los
    // productos de esa página no abren ningún color): se sigue con la próxima en lugar de navegar.
    const responseGrid = html.querySelector('[data-on-grid]');
    const items = html.querySelectorAll('[data-on-grid] > .on-grid__item');
    const grid = this.grid;
    if (!grid || !responseGrid) throw new Error('empty');

    // Mobile: lo que quedó oculto de la primera página aparece junto con la siguiente.
    const firstHidden = Array.from(grid.children).find((item) => item.offsetParent === null);
    const firstNew = firstHidden || items[0];
    grid.removeAttribute('data-on-cut-m');
    grid.append(...items);
    if (!restoring) firstNew?.querySelector('.on-card__link')?.focus({ preventScroll: true });

    const progress = html.querySelector('[data-on-progress]');
    const currentProgress = this.querySelector('[data-on-progress]');
    if (progress && currentProgress) currentProgress.textContent = progress.textContent;

    const nextLink = html.querySelector('[data-on-load-more]');
    if (nextLink) {
      link.href = nextLink.getAttribute('href');
      link.removeAttribute('aria-busy');
    } else {
      link.remove();
    }
    return true;
  }

  /* Al volver desde la ficha la página se vuelve a pedir con la primera tanda: las páginas
     sumadas con «VER MÁS» y el scroll quedan en el history.state de esta entrada. */
  get savedState() {
    const saved = history.state?.onLoadMore;
    return saved?.section === this.dataset.sectionId ? saved : null;
  }

  saveState(changes) {
    const state = history.state && typeof history.state === 'object' ? history.state : {};
    const saved = this.savedState || { section: this.dataset.sectionId, pages: [], scrollY: 0 };
    history.replaceState({ ...state, onLoadMore: { ...saved, ...changes } }, '');
  }

  remember(href) {
    const pages = [...(this.savedState?.pages || []), href];
    this.saveState({ pages, scrollY: pageScrollTop() });
    history.scrollRestoration = 'manual';
    window.addEventListener('pagehide', this.saveScroll);
  }

  saveScroll = () => {
    if (this.savedState) this.saveState({ scrollY: pageScrollTop() });
  };

  async restore() {
    const saved = this.savedState;
    if (!saved?.pages.length) return;
    history.scrollRestoration = 'manual';
    window.addEventListener('pagehide', this.saveScroll);

    for (const href of saved.pages) {
      const link = /** @type {HTMLAnchorElement | null} */ (this.querySelector('[data-on-load-more]'));
      if (!link) break;
      link.href = href;
      try {
        if (!(await this.appendPage(link, { restoring: true }))) return;
      } catch {
        link.removeAttribute('aria-busy');
        break;
      }
    }
    // Sin animación: .page-wrapper tiene scroll-behavior: smooth
    (pageScroller() ?? window).scrollTo({ top: saved.scrollY, behavior: 'instant' });
  }
}

class OnSort extends HTMLElement {
  connectedCallback() {
    this.toggle = this.querySelector('[data-on-sort-toggle]');
    this.list = this.querySelector('.on-sort__list');
    this.toggle?.addEventListener('click', this.onToggle);
    this.list?.addEventListener('click', this.onSelect);
    this.addEventListener('keydown', this.onKeydown);
    document.addEventListener('click', this.onDocumentClick);
  }

  disconnectedCallback() {
    document.removeEventListener('click', this.onDocumentClick);
  }

  get isOpen() {
    return this.toggle?.getAttribute('aria-expanded') === 'true';
  }

  open() {
    if (!this.list || !this.toggle) return;
    this.list.hidden = false;
    this.toggle.setAttribute('aria-expanded', 'true');
  }

  close({ focus = false } = {}) {
    if (!this.list || !this.toggle || !this.isOpen) return;
    this.list.hidden = true;
    this.toggle.setAttribute('aria-expanded', 'false');
    if (focus) this.toggle.focus();
  }

  onToggle = () => {
    if (this.isOpen) {
      this.close();
    } else {
      this.open();
    }
  };

  /* El link lleva sólo ?sort_by; se conservan los filtros que ya están en la URL. */
  onSelect = (event) => {
    const option = /** @type {HTMLElement} */ (event.target).closest('a[data-value]');
    if (!option) return;
    event.preventDefault();
    const url = new URL(window.location.href);
    url.searchParams.set('sort_by', option.getAttribute('data-value'));
    url.searchParams.delete('page');
    this.close();
    window.location.href = url.toString();
  };

  onKeydown = (event) => {
    if (event.key === 'Escape' && this.isOpen) {
      event.stopPropagation();
      this.close({ focus: true });
    }
  };

  onDocumentClick = (event) => {
    if (!this.contains(/** @type {Node} */ (event.target))) this.close();
  };
}

/**
 * Panel de filtros. Modal en todos los anchos (foco atrapado, Escape y clic en el velo cierran,
 * la página no recibe clics ni scrollea), entra desde la derecha. Cada filtro se aplica apenas se
 * elige, sin cerrar el panel (como only.com.uy): Horizon cambia la URL y re-renderiza la sección
 * de fondo (grilla, «N productos» y cantidades de cada opción). El precio se aplica al soltar el
 * riel, con Enter o tras una pausa al escribir. APLICAR (Figma) sólo cierra: no hace falta para
 * filtrar. El panel y su estado sobreviven al re-render: el <dialog>, el cuerpo de cada grupo y
 * [VER MÁS] no se tocan (data-skip-node-update) y el precio que se está escribiendo tampoco
 * (data-skip-value-update).
 */
class OnFilters extends HTMLElement {
  /** @type {HTMLElement | null} */
  trigger = null;
  closing = false;
  /** @type {number | undefined} */
  priceTimer = undefined;
  /** Filtros y orden de lo que está dibujado (filterQuery). */
  renderedQuery = '';

  connectedCallback() {
    this.addEventListener('click', this.onClick);
    this.addEventListener('submit', this.onSubmit);
    this.addEventListener('input', this.onInput);
    this.addEventListener('change', this.onChange);
    this.addEventListener('keydown', this.onKeydown);
    // formdata, cancel y close no burbujean: se escuchan en captura.
    this.addEventListener('formdata', this.onFormData, true);
    this.addEventListener('cancel', this.onCancel, true);
    this.addEventListener('close', this.onDialogClose, true);
    window.addEventListener('popstate', this.onPopState);
    this.renderedQuery = filterQuery(new URLSearchParams(window.location.search));
  }

  disconnectedCallback() {
    window.removeEventListener('popstate', this.onPopState);
    window.clearTimeout(this.priceTimer);
    if (this.dialog) unlockScroll(this.dialog);
  }

  /** @returns {HTMLDialogElement | null} */
  get dialog() {
    return this.querySelector('[data-on-filters-dialog]');
  }

  /** @returns {HTMLFormElement | null} */
  get form() {
    return this.querySelector('[data-on-filters-form]');
  }

  /** El <facets-form-component> de Horizon, si facets.js ya lo registró. */
  get facets() {
    const component = /** @type {any} */ (this.querySelector('facets-form-component'));
    return typeof component?.updateFilters === 'function' ? component : null;
  }

  /** Lo que el formulario mandaría (el precio en los extremos no viaja: onFormData). */
  formQuery() {
    const form = this.form;
    return form ? filterQuery(new URLSearchParams(/** @type {any} */ (new FormData(form)))) : '';
  }

  /**
   * Aplica lo elegido con la lógica de Horizon: entrada nueva en el historial con los filtros,
   * re-render de la sección y evento estándar de colección. Si no cambió nada, no hace nada.
   */
  apply() {
    window.clearTimeout(this.priceTimer);
    this.priceTimer = undefined;
    const facets = this.facets;
    if (!facets) return; // Sin facets.js se filtra con APLICAR (GET a la colección).
    const query = this.formQuery();
    if (query === this.renderedQuery) return;
    this.renderedQuery = query;
    facets.updateFilters();
  }

  /**
   * El precio espera una pausa: escribir «150000» o mover el riel con el teclado no aplica cada paso.
   * @param {HTMLInputElement} [field] - el campo que se está escribiendo
   */
  applyLater(field) {
    window.clearTimeout(this.priceTimer);
    this.priceTimer = window.setTimeout(() => {
      // Si mientras tanto llegó otro re-render, el riel volvió al precio aplicado; el campo conserva
      // lo escrito (data-skip-value-update) y se vuelve a pasar al riel.
      if (field?.isConnected) this.fieldToRange(field, false);
      this.apply();
    }, PRICE_DELAY);
  }

  /* Atrás y adelante: Horizon cambia la URL pero no vuelve a dibujar la sección. Se re-renderiza con
     los filtros de esa entrada (si ya se vio sale del caché de Horizon) y se vuelven a sumar las
     tandas que se habían cargado en ella. */
  onPopState = () => {
    const facets = this.facets;
    if (!facets) return;
    const query = filterQuery(new URLSearchParams(window.location.search));
    if (query === this.renderedQuery) return;
    this.renderedQuery = query;
    window.clearTimeout(this.priceTimer);
    this.priceTimer = undefined;
    sectionRenderer
      .renderSection(facets.sectionId)
      .then(() => /** @type {OnCollection | null} */ (this.closest('on-collection'))?.restore())
      .catch(() => window.location.reload());
  };

  /** @param {HTMLElement} [trigger] */
  open(trigger) {
    const dialog = this.dialog;
    if (!dialog || dialog.open) return;
    this.trigger = trigger ?? /** @type {HTMLElement | null} */ (document.activeElement);
    // Lo tildado es lo que está dibujado (también con un orden por defecto que la URL no lleva).
    this.renderedQuery = this.formQuery();
    dialog.showModal();
    lockScroll(dialog);
    // Un reflow antes de la clase para que la entrada (y el velo) animen.
    void dialog.offsetWidth;
    dialog.classList.add('is-open');
    /** @type {HTMLElement | null} */ (dialog.querySelector('[data-on-filters-close]'))?.focus();
  }

  async close() {
    const dialog = this.dialog;
    if (!dialog?.open || this.closing) return;
    this.closing = true;
    // El precio que quedó esperando la pausa se aplica al cerrar.
    this.apply();
    dialog.classList.remove('is-open');
    if (!prefersReducedMotion()) {
      await new Promise((resolve) => {
        const done = () => {
          dialog.removeEventListener('transitionend', onEnd);
          resolve(undefined);
        };
        const onEnd = (/** @type {TransitionEvent} */ event) => {
          if (event.target === dialog && event.propertyName === 'transform') done();
        };
        dialog.addEventListener('transitionend', onEnd);
        window.setTimeout(done, 350);
      });
    }
    this.closing = false;
    if (dialog.open) dialog.close();
  }

  /* Limpieza de cualquier cierre (también el que fuerza el navegador con Escape). */
  onDialogClose = (/** @type {Event} */ event) => {
    const dialog = this.dialog;
    if (!dialog || event.target !== dialog) return;
    dialog.classList.remove('is-open');
    unlockScroll(dialog);
    this.apply();
    const trigger = this.trigger?.isConnected
      ? this.trigger
      : /** @type {HTMLElement | null} */ (document.querySelector(`[aria-controls="${dialog.id}"]`));
    trigger?.focus({ preventScroll: true });
    this.trigger = null;
  };

  onCancel = (/** @type {Event} */ event) => {
    if (event.target !== this.dialog) return;
    event.preventDefault();
    this.close();
  };

  onClick = (/** @type {MouseEvent} */ event) => {
    const target = /** @type {HTMLElement} */ (event.target);
    const dialog = this.dialog;

    // Los clics en el velo llegan al <dialog>, afuera de su caja.
    if (dialog && target === dialog) {
      const { left, right, top, bottom } = dialog.getBoundingClientRect();
      const inside = event.clientX >= left && event.clientX <= right && event.clientY >= top && event.clientY <= bottom;
      if (!inside) this.close();
      return;
    }

    if (target.closest('[data-on-filters-close]')) {
      this.close();
      return;
    }

    const more = /** @type {HTMLElement | null} */ (target.closest('[data-on-filters-more]'));
    if (more) {
      const expanded = more.getAttribute('aria-expanded') !== 'true';
      more.setAttribute('aria-expanded', String(expanded));
      more.closest('.on-filters__body')?.toggleAttribute('data-expanded', expanded);
      const label = more.querySelector('[data-on-filters-more-label]');
      if (label) label.textContent = (expanded ? more.dataset.less : more.dataset.more) ?? '';
      return;
    }

    // [BORRAR FILTROS]: como cualquier filtro, se aplica con el panel abierto (conserva el orden).
    if (target.closest('[data-on-filters-clear]')) {
      const form = this.form;
      if (!this.facets || !form) return; // Sin facets.js el link lleva a la colección sin filtros.
      event.preventDefault();
      for (const input of form.querySelectorAll('input[type="checkbox"]')) {
        /** @type {HTMLInputElement} */ (input).checked = false;
      }
      for (const price of form.querySelectorAll('[data-on-price]')) this.resetPrice(/** @type {HTMLElement} */ (price));
      this.apply();
    }
  };

  /* APLICAR: lo elegido ya está aplicado; confirma el precio a medio escribir y cierra. */
  onSubmit = (/** @type {SubmitEvent} */ event) => {
    if (!this.facets) return; // Sin facets.js el formulario navega (GET).
    event.preventDefault();
    for (const field of this.querySelectorAll('[data-on-price-field]')) this.fieldToRange(/** @type {HTMLInputElement} */ (field), true);
    this.close();
  };

  /* Enter en un campo de precio aplica sin cerrar (si no, el formulario lo tomaría como APLICAR). */
  onKeydown = (/** @type {KeyboardEvent} */ event) => {
    const target = /** @type {HTMLElement} */ (event.target);
    if (event.key !== 'Enter' || !target.matches('[data-on-price-field]')) return;
    event.preventDefault();
    this.fieldToRange(/** @type {HTMLInputElement} */ (target), true);
    this.apply();
  };

  /* Precio en los extremos = sin filtro de precio: no viaja en la URL. */
  onFormData = (/** @type {FormDataEvent} */ event) => {
    for (const price of this.querySelectorAll('[data-on-price]')) {
      const max = Number(/** @type {HTMLElement} */ (price).dataset.max);
      for (const range of price.querySelectorAll('[data-on-price-range]')) {
        const input = /** @type {HTMLInputElement} */ (range);
        const atEdge = input.dataset.onPriceRange === 'from' ? Number(input.value) <= 0 : Number(input.value) >= max;
        if (atEdge) event.formData.delete(input.name);
      }
    }
  };

  onInput = (/** @type {Event} */ event) => {
    const target = /** @type {HTMLInputElement} */ (event.target);
    if (target.matches('[data-on-price-range]')) {
      this.syncPrice(target);
    } else if (target.matches('[data-on-price-field]')) {
      this.fieldToRange(target, false);
      this.applyLater(target);
    }
  };

  /* Tildes y orden: al instante. Riel de precio (al soltarlo o con las flechas): tras la pausa.
     Campo de precio: al confirmarlo (blur). */
  onChange = (/** @type {Event} */ event) => {
    const target = /** @type {HTMLInputElement} */ (event.target);
    if (target.matches('[data-on-price-range]')) {
      this.applyLater();
      return;
    }
    if (target.matches('[data-on-price-field]')) this.fieldToRange(target, true);
    if (target.closest('[data-on-filters-form]')) this.apply();
  };

  /** @param {HTMLElement} price */
  ranges(price) {
    return {
      from: /** @type {HTMLInputElement} */ (price.querySelector('[data-on-price-range="from"]')),
      to: /** @type {HTMLInputElement} */ (price.querySelector('[data-on-price-range="to"]')),
    };
  }

  /** @param {HTMLElement} price @param {number} value */
  format(price, value) {
    const digits = String(Math.round(value)).replace(/\B(?=(\d{3})+(?!\d))/g, '.');
    return `${price.dataset.symbol ?? '$'}${digits}`;
  }

  /**
   * Ajusta el par de range (sin cruzarse), el tramo del riel y los campos.
   * @param {HTMLInputElement} [moved] - el range que se movió
   * @param {HTMLElement} [container]
   */
  syncPrice(moved, container) {
    const price = container ?? /** @type {HTMLElement | null} */ (moved?.closest('[data-on-price]'));
    if (!price) return;
    const { from, to } = this.ranges(price);
    if (!from || !to) return;
    const max = Number(price.dataset.max) || 1;
    let low = Number(from.value);
    let high = Number(to.value);
    if (low > high) {
      if (moved === from) from.value = String((low = high));
      else to.value = String((high = low));
    }
    // Con los dos tiradores juntos al final, el de «desde» queda arriba para poder moverlo.
    from.toggleAttribute('data-on-top', low >= max);
    price.style.setProperty('--on-from', String((low / max) * 100));
    price.style.setProperty('--on-to', String((high / max) * 100));
    from.setAttribute('aria-valuetext', this.format(price, low));
    to.setAttribute('aria-valuetext', this.format(price, high));
    for (const [range, value] of [
      ['from', low],
      ['to', high],
    ]) {
      const field = /** @type {HTMLInputElement | null} */ (price.querySelector(`[data-on-price-field="${range}"]`));
      if (field && document.activeElement !== field) field.value = this.format(price, Number(value));
    }
  }

  /**
   * Lo que se escribe en un campo mueve su range; al confirmar se reformatea.
   * @param {HTMLInputElement} field
   * @param {boolean} commit
   */
  fieldToRange(field, commit) {
    const price = /** @type {HTMLElement | null} */ (field.closest('[data-on-price]'));
    if (!price) return;
    const { from, to } = this.ranges(price);
    const side = field.dataset.onPriceField;
    const range = side === 'from' ? from : to;
    if (!range) return;
    const digits = field.value.replace(/\D/g, '');
    const max = Number(price.dataset.max) || 0;
    let value = digits === '' ? (side === 'from' ? 0 : max) : Math.min(Number(digits), max);
    if (commit) {
      if (side === 'from') value = Math.min(value, Number(to?.value ?? max));
      else value = Math.max(value, Number(from?.value ?? 0));
    }
    range.value = String(value);
    // Mientras se escribe el range no cruza al otro (se frena); al confirmar el campo muestra el valor final.
    this.syncPrice(range, price);
    if (commit) field.value = this.format(price, Number(range.value));
  }

  /** @param {HTMLElement} price */
  resetPrice(price) {
    const { from, to } = this.ranges(price);
    if (from) from.value = '0';
    if (to) to.value = price.dataset.max ?? to.max;
    this.syncPrice(undefined, price);
    for (const field of price.querySelectorAll('[data-on-price-field]')) {
      const input = /** @type {HTMLInputElement} */ (field);
      input.value = this.format(price, input.dataset.onPriceField === 'from' ? 0 : Number(price.dataset.max));
    }
  }
}

class OnProductCard extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector('[data-on-card-track]');
    this.prev = this.querySelector('[data-on-card-prev]');
    this.next = this.querySelector('[data-on-card-next]');
    this.toggleButton = this.querySelector('[data-on-card-toggle]');
    this.status = this.querySelector('[data-on-card-status]');

    this.addEventListener('click', this.onClick);
    this.addEventListener('keydown', this.onKeydown);
    this.updateArrows();
  }

  disconnectedCallback() {
    document.removeEventListener('click', this.onDocumentClick);
  }

  /* Las fotos van en loop (como el Figma, con las dos flechas siempre): sólo se ocultan si hay una foto. */
  updateArrows = () => {
    if (!this.track || !this.prev || !this.next) return;
    const single = this.track.scrollWidth <= this.track.clientWidth + 1;
    this.prev.hidden = single;
    this.next.hidden = single;
  };

  slide(direction) {
    if (!this.track) return;
    const { scrollLeft, scrollWidth, clientWidth } = this.track;
    const atStart = scrollLeft <= 1;
    const atEnd = scrollLeft + clientWidth >= scrollWidth - 1;
    if (direction > 0 && atEnd) return this.track.scrollTo({ left: 0, behavior: 'smooth' });
    if (direction < 0 && atStart) return this.track.scrollTo({ left: scrollWidth, behavior: 'smooth' });
    this.track.scrollBy({ left: direction * clientWidth, behavior: 'smooth' });
  }

  setQuickOpen(open) {
    this.classList.toggle('is-quick-open', open);
    this.toggleButton?.setAttribute('aria-expanded', String(open));
    if (open) {
      document.addEventListener('click', this.onDocumentClick);
      this.querySelector('.on-card__size:not(:disabled)')?.focus({ preventScroll: true });
    } else {
      document.removeEventListener('click', this.onDocumentClick);
    }
  }

  onDocumentClick = (event) => {
    if (!this.contains(/** @type {Node} */ (event.target))) this.setQuickOpen(false);
  };

  onKeydown = (event) => {
    if (event.key === 'Escape' && this.classList.contains('is-quick-open')) {
      event.stopPropagation();
      this.setQuickOpen(false);
      this.toggleButton?.focus();
    }
  };

  onClick = (event) => {
    const target = /** @type {HTMLElement} */ (event.target);
    if (target.closest('[data-on-card-prev]')) return this.slide(-1);
    if (target.closest('[data-on-card-next]')) return this.slide(1);
    if (target.closest('[data-on-card-toggle]')) return this.setQuickOpen(!this.classList.contains('is-quick-open'));

    const size = /** @type {HTMLButtonElement | null} */ (target.closest('.on-card__size'));
    if (size && !size.disabled) this.add(size);
  };

  setStatus(text, isError = false) {
    if (!this.status) return;
    this.status.textContent = text;
    this.status.classList.toggle('is-error', isError);
  }

  async add(button) {
    if (button.getAttribute('aria-busy') === 'true') return;
    const strings = window.OnStrings || {};
    const variantId = button.dataset.variantId;
    button.setAttribute('aria-busy', 'true');
    this.setStatus(strings.adding || '…');

    const sectionIds = [...document.querySelectorAll('cart-items-component')]
      .map((element) => /** @type {HTMLElement} */ (element).dataset.sectionId)
      .filter(Boolean);

    let resolveEvent;
    let rejectEvent;
    const promise = new Promise((resolve, reject) => {
      resolveEvent = resolve;
      rejectEvent = reject;
    });
    promise.catch(() => {});

    const events = await import('@shopify/events').catch(() => null);
    const CartLinesUpdateEvent = events?.CartLinesUpdateEvent;
    if (CartLinesUpdateEvent) {
      this.dispatchEvent(
        new CartLinesUpdateEvent({
          action: 'add',
          context: 'product',
          lines: [{ merchandiseId: variantId, quantity: 1 }],
          promise,
        })
      );
    }

    const root = window.Shopify?.routes?.root ?? '/';
    try {
      const response = await fetch(`${root}cart/add.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          items: [{ id: Number(variantId), quantity: 1 }],
          sections: sectionIds.join(','),
          sections_url: window.location.pathname,
        }),
      });
      const data = await response.json();
      const cart = await fetch(`${root}cart.js`).then((r) => r.json());

      if (!response.ok || data.status) {
        // 422: sin stock suficiente u otro motivo; se muestra el mensaje de Shopify acá.
        this.setStatus(data.description || data.message || strings.error || '', true);
        resolveEvent?.({
          cart: CartLinesUpdateEvent?.createCartFromAjaxResponse?.(cart),
          detail: { didError: true, items: cart.items, source: 'on-product-card', productId: this.dataset.productId },
        });
        return;
      }

      button.classList.add('is-added');
      this.setStatus(strings.added || '✓');
      resolveEvent?.({
        cart: CartLinesUpdateEvent?.createCartFromAjaxResponse?.(cart),
        detail: {
          didError: false,
          items: cart.items,
          itemCount: 1,
          source: 'on-product-card',
          productId: this.dataset.productId,
          sections: data.sections,
        },
      });
      window.setTimeout(() => {
        button.classList.remove('is-added');
        this.setStatus(this.status?.dataset.default || '');
      }, 2500);
    } catch (error) {
      this.setStatus(strings.error || '', true);
      rejectEvent?.(error);
    } finally {
      button.removeAttribute('aria-busy');
    }
  }
}

if (!customElements.get('on-collection')) customElements.define('on-collection', OnCollection);
if (!customElements.get('on-sort')) customElements.define('on-sort', OnSort);
if (!customElements.get('on-filters')) customElements.define('on-filters', OnFilters);
if (!customElements.get('on-product-card')) customElements.define('on-product-card', OnProductCard);
